import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { enrichApolloLeadsForExport, getApolloLeadDetail } from '@ndi/leads/api/apollo-lead-detail-ops.js';
import { searchApolloLeads } from '@ndi/leads/api/leads.js';
import { ApolloLeadSearchInput } from '@ndi/leads/baseql/apollo-lead-search.js';
import { getNames, registerLocale } from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json' with { type: 'json' };
import { html, nothing } from 'lit';
import { getCrmConfig } from '../db/baseql/crm-ops.js';
import { downloadTextFile, rowsToCsv } from '../lead-csv-export.js';
import { clearIcpControlledFields } from '../icp-reinterpret.js';
import {
  CSV_FIELD_SLOT_LABELS,
  CSV_FIELD_SLOTS,
  csvSourcingOverlay,
  describeColumnsForClassifier,
  mergeCsvOverlay,
  normalizeClassifications,
  readCsvColumns,
  UNMAPPED_SLOT,
  type CsvSourcingField,
} from '../csv-sourcing-fields.js';
import { csvRowsToLeads, parseCsvText, parseExcelFile, readFileAsArrayBuffer, readFileAsText } from '../lead-csv.js';
import {
  curatedSourcedLeads,
  fetchedApolloPersonIds,
  readRemovedApolloPersonIds,
  unionExcludeIds,
} from '../sourcing-curation.js';
import { toast } from '../toast.js';

registerLocale(enLocale);

export interface LeadRecord {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  jobTitle: string;
  linkedinUrl?: string;
  industry?: string;
  /** Real employee count of the verified company (Apollo org-scoped sourcing). */
  employeeCount?: number;
  apolloPersonId?: string;
  hasEmail?: boolean;
  hasDirectPhone?: string;
  /** Pre-existing HubSpot contact id when imported from a HubSpot list. */
  crmContactId?: string;
  /** 'csv' | 'hubspot' — origin of this lead row, surfaced in the preview. */
  source?: 'csv' | 'hubspot' | 'apollo' | 'eigen';
}

export interface WizLeadsProps {
  campaignName: string;
  onCampaignNameChange: (name: string) => void;
  leads: LeadRecord[];
  onLeadsChange: (leads: LeadRecord[]) => void;
  /**
   * Type of the surrounding campaign. Used to warn the user when imported
   * leads are missing channel-specific data (LinkedIn URL / email) and would
   * therefore be skipped during message generation — see `campaign-prepare.ts`
   * `leadsForGeneration` filters on the backend.
   */
  campaignType?: 'email' | 'linkedin' | 'multi' | null;
  apolloSearchInput?: Record<string, unknown>;
  onApolloSearchInputChange?: (input: Record<string, unknown> | undefined) => void;
  /** Project this campaign belongs to — undefined means the default project (no cross-campaign dedup). */
  projectId?: string;
  onProjectIdChange?: (projectId: string | undefined) => void;
}

interface ProjectSummary {
  _id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
}

interface SourcingParams {
  icpDescription: string;
  companyLocation: string[];
  companyIndustry: string[];
  industryKeywords: string;
  employeeCount: string[];
  revenueMin: number | null;
  revenueMax: number | null;
  jobTitle: string;
  department: string[];
  personLocation: string[];
  /**
   * Companies to source INSIDE — typically classified out of an uploaded file.
   * Not an ICP-interpreted field: it is input the operator supplied, so
   * "Re-interpret ICP" deliberately leaves it alone (see icp-reinterpret.ts).
   * Empty means the search says nothing about the company, never "match none".
   */
  organizationNames: string[];
  leadLimit: string;
}

type ApolloSearchPayload = Record<string, unknown> & SourcingParams & { page: number; perPage: number };

/**
 * Values shown as removable chips per uploaded column. The rest are still USED —
 * the label says so — this only keeps a 200-value column from filling the step.
 */
const CSV_CHIPS_SHOWN = 25;

const DEFAULT_APOLLO_LEAD_LIMIT = 100;
const MAX_APOLLO_LEAD_LIMIT = 1000;

const emptySourcingParams = (): SourcingParams => ({
  icpDescription: '',
  companyLocation: [],
  companyIndustry: [],
  industryKeywords: '',
  employeeCount: [],
  revenueMin: null,
  revenueMax: null,
  jobTitle: '',
  department: [],
  personLocation: [],
  organizationNames: [],
  leadLimit: String(DEFAULT_APOLLO_LEAD_LIMIT),
});

const stringValue = (value: unknown): string => (typeof value === 'string' ? value : '');

const coerceToStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const sourcingParamsFromApolloInput = (input: Record<string, unknown> | undefined): SourcingParams => ({
  ...emptySourcingParams(),
  icpDescription: stringValue(input?.icpDescription),
  companyLocation: coerceToStringArray(input?.companyLocation),
  companyIndustry: coerceToStringArray(input?.companyIndustry),
  industryKeywords: stringValue(input?.industryKeywords),
  employeeCount: coerceToStringArray(input?.employeeCount),
  revenueMin: typeof input?.revenueMin === 'number' ? input.revenueMin : null,
  revenueMax: typeof input?.revenueMax === 'number' ? input.revenueMax : null,
  jobTitle: stringValue(input?.jobTitle),
  department: coerceToStringArray(input?.department),
  personLocation: coerceToStringArray(input?.personLocation),
  organizationNames: coerceToStringArray(input?.organizationNames),
  leadLimit: stringValue(input?.leadLimit) || String(DEFAULT_APOLLO_LEAD_LIMIT),
});

// Page-1 size of the sourcing preview list the operator curates. "Load more"
// pages beyond it. This perPage also rides into the persisted draft payload, but
// there it is inert — campaign-prepare and the CSV export each compute their own
// count and never read the draft's perPage.
const PREVIEW_PAGE_SIZE = 25;

const apolloSearchPayload = (params: SourcingParams): ApolloSearchPayload => ({
  ...params,
  page: 1,
  perPage: PREVIEW_PAGE_SIZE,
});

/**
 * Rehydrate the auto-fill snapshot persisted inside the draft payload. Without
 * it, re-entering the wizard forgets WHICH field values came from a prior ICP
 * interpretation — they then count as user input, survive `withoutStaleAutoFill`
 * and get unioned into every later interpretation (the "changed my ICP but the
 * old criteria keep coming back" bug).
 */
const autoFilledFromApolloInput = (input: Record<string, unknown> | undefined): Partial<SourcingParams> => {
  const raw = input?.autoFilled;
  if (!raw || typeof raw !== 'object') return {};
  const src = raw as Record<string, unknown>;
  const out: Partial<SourcingParams> = {};
  for (const key of ['companyLocation', 'companyIndustry', 'employeeCount', 'department', 'personLocation'] as const) {
    if (Array.isArray(src[key])) out[key] = coerceToStringArray(src[key]);
  }
  for (const key of ['jobTitle', 'industryKeywords'] as const) {
    if (typeof src[key] === 'string') out[key] = src[key] as string;
  }
  for (const key of ['revenueMin', 'revenueMax'] as const) {
    if (typeof src[key] === 'number') out[key] = src[key] as number;
  }
  return out;
};

const normalizeLeadLimit = (value: string | null | undefined): string => {
  const parsed = Number.parseInt(value ?? '', 10);
  if (!Number.isFinite(parsed)) return String(DEFAULT_APOLLO_LEAD_LIMIT);
  return String(Math.min(MAX_APOLLO_LEAD_LIMIT, Math.max(1, parsed)));
};

const apolloLeadSearchInput = (
  input: ApolloSearchPayload,
  opts?: { forceRemap?: boolean; excludeApolloPersonIds?: string[] },
): ApolloLeadSearchInput => {
  const entity = new ApolloLeadSearchInput({});
  entity.icpDescription = input.icpDescription;
  entity.companyLocation = input.companyLocation;
  entity.companyIndustry = input.companyIndustry;
  entity.industryKeywords = input.industryKeywords;
  entity.employeeCount = input.employeeCount;
  entity.revenueMin = input.revenueMin != null ? input.revenueMin * 1000000 : undefined;
  entity.revenueMax = input.revenueMax != null ? input.revenueMax * 1000000 : undefined;
  entity.jobTitle = input.jobTitle;
  entity.department = input.department;
  entity.personLocation = input.personLocation;
  entity.page = input.page;
  entity.perPage = input.perPage;
  // Transient flags — intentionally NOT part of the persisted draft payload.
  if (opts?.forceRemap) entity.forceRemap = true;
  if (opts?.excludeApolloPersonIds?.length) entity.excludeApolloPersonIds = opts.excludeApolloPersonIds;
  return entity;
};

// Sorted alphabetically for scannability (tester feedback 01.07). Covers the
// Ema services/software list plus European-relevant industries.
const COMPANY_INDUSTRIES = [
  'Accounting Services',
  'Advertising & Marketing',
  'Aerospace & Defense',
  'Artificial Intelligence',
  'Automotive',
  'Banking',
  'Biotechnology',
  'Business Intelligence (BI) Software',
  'Business Services',
  'Call Centers & Business Centers',
  'Chemicals',
  'Cloud Computing',
  'Colleges & Universities',
  'Construction',
  'Consumer Goods',
  'Content & Collaboration Software',
  'Credit Cards & Transaction Processing',
  'Custom Software & IT Services',
  'Customer Relationship Management (CRM) Software',
  'Cybersecurity',
  'Data Collection & Internet Portals',
  'Database & File Management Software',
  'Debt Collection',
  'E-commerce',
  'Education',
  'Engineering Software',
  'Enterprise Resource Planning (ERP) Software',
  'Financial Software',
  'Government',
  'Healthcare Services',
  'Healthcare Software',
  'Holding Companies & Conglomerates',
  'Hospitality',
  'Hospitals & Physicians Clinics',
  'Human Resources Software',
  'Industrial Automation',
  'Insurance',
  'Investment Banking',
  'K-12 Schools',
  'Law Firms & Legal Services',
  'Legal Software',
  'Lending & Brokerage',
  'Logistics',
  'Machinery',
  'Management Consulting',
  'Manufacturing',
  'Media & Internet',
  'Medical Devices',
  'Medical Laboratories & Imaging Centers',
  'Mobile App Development',
  'Multimedia, Games & Graphics Software',
  'Networking Software',
  'Non-Profit',
  'Pharmaceuticals',
  'Real Estate',
  'Renewable Energy',
  'Retail',
  'Security Products & Services',
  'Security Software',
  'Social Networks',
  'Software',
  'Storage & System Management Software',
  'Supply Chain Management (SCM) Software',
  'Telecommunications',
  'Training',
  'Transportation',
  'Utilities',
  'Venture Capital & Private Equity',
  'Wholesale',
];
const EMPLOYEE_COUNTS = ['1-50', '51-200', '201-500', '501-1000', '1000-5000', '5000+'];
// NOTE: entries must not contain commas — department values are joined
// comma-separated before being handed to the ICP→Apollo mapper, so a comma
// inside one entry silently splits it into two. 'Finance, Operations' was one
// glued entry ("dropdown not working" tester feedback 01.07); C-Suite was
// missing entirely even though the test persona filtered on it.
const DEPARTMENTS = [
  'C-Suite',
  'Engineering',
  'Finance',
  'Human Resources',
  'IT',
  'Legal',
  'Marketing',
  'Operations',
  'Product',
  'Sales',
];

const ALL_COUNTRY_NAMES: string[] = (() => {
  // 'alias' returns the common short names ("United Kingdom", "United States")
  // instead of the official long forms ("United Kingdom of Great Britain and
  // Northern Ireland") — testers could not find UK/USA in the list.
  const names = getNames('en', { select: 'alias' });
  const list = (Object.values(names) as string[]).map(name => {
    // Append the common abbreviation so typing "UK" / "USA" finds the entry
    // (the dropdown search is a plain substring match on the label).
    if (name === 'United Kingdom') return 'United Kingdom (UK)';
    if (name === 'United States') return 'United States (USA)';
    return name;
  });
  // UK constituent countries — Apollo knows them as regions; the server maps
  // them to Apollo-compatible location strings (see LOCATION_ALIASES).
  list.push('England', 'Scotland', 'Wales', 'Northern Ireland');
  return list.sort((a, b) => a.localeCompare(b));
})();

// ---------------------------------------------------------------------------
// CSV Export
// ---------------------------------------------------------------------------

const CSV_EXPORT_COLUMNS: { header: string; get: (lead: LeadRecord) => string }[] = [
  { header: 'First Name', get: l => l.firstName },
  { header: 'Last Name', get: l => l.lastName },
  { header: 'Job Title', get: l => l.jobTitle },
  { header: 'Company', get: l => l.company },
  { header: 'Industry', get: l => l.industry ?? '' },
  { header: 'Employee Count', get: l => (l.employeeCount != null ? String(l.employeeCount) : '') },
  { header: 'LinkedIn URL', get: l => l.linkedinUrl ?? '' },
  // Apollo's people search doesn't return real email addresses (enrichment is
  // a separate, per-lead call) — the column stays blank for sourced leads by
  // design, not an error.
  { header: 'Email', get: l => l.email ?? '' },
];

function leadsToCsv(leads: LeadRecord[]): string {
  return rowsToCsv(
    CSV_EXPORT_COLUMNS.map(c => c.header),
    leads.map(lead => CSV_EXPORT_COLUMNS.map(c => c.get(lead))),
  );
}

export const WizLeads = stateful((props: WizLeadsProps) => {
  const {
    campaignName,
    onCampaignNameChange,
    leads,
    onLeadsChange,
    campaignType,
    apolloSearchInput,
    onApolloSearchInputChange,
    projectId,
    onProjectIdChange,
  } = props;
  const [dragging, setDragging] = useState(false);
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [savingNewProject, setSavingNewProject] = useState(false);
  // Apollo person ids already sourced elsewhere in the selected (non-default)
  // project — passed into every search below so a second campaign in the
  // same project never re-surfaces a lead the first one already has.
  const [excludeApolloPersonIds, setExcludeApolloPersonIds] = useState<string[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const resp = await fetch('/api/projects');
        const data = await resp.json();
        if (data.ok) setProjects(data.projects);
      } catch {
        /* ignore — project picker just stays empty, defaults to Default Project */
      }
    })();
  }, []);

  useEffect(() => {
    if (!projectId) {
      setExcludeApolloPersonIds([]);
      return;
    }
    const selected = projects?.find(p => p._id === projectId);
    if (selected?.isDefault) {
      setExcludeApolloPersonIds([]);
      return;
    }
    void (async () => {
      try {
        const resp = await fetch(`/api/projects/${encodeURIComponent(projectId)}/apollo-person-ids`);
        const data = await resp.json();
        if (data.ok) setExcludeApolloPersonIds(data.apolloPersonIds ?? []);
      } catch {
        setExcludeApolloPersonIds([]);
      }
    })();
  }, [projectId, projects]);

  const createProject = async () => {
    if (!newProjectName.trim()) return;
    setSavingNewProject(true);
    try {
      const resp = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newProjectName.trim(), description: newProjectDescription.trim() || undefined }),
      });
      const data = await resp.json();
      if (!data.ok) {
        toast.error(data.error ?? 'Failed to create project');
        return;
      }
      setProjects([...(projects ?? []), data.project]);
      onProjectIdChange?.(data.project._id);
      setCreatingProject(false);
      setNewProjectName('');
      setNewProjectDescription('');
    } catch {
      toast.error('Failed to create project (network error)');
    }
    setSavingNewProject(false);
  };
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [selectedLeadSource, setSelectedLeadSource] = useState<'sourcing' | 'hubspot' | 'csv'>('sourcing');
  // A file uploaded to STEER the sourcing (companies, sites, segments) rather
  // than to import leads. Transient on purpose: what the operator keeps ends up
  // in `sourcingParams`, which is what the draft persists and launch re-reads.
  const [csvFields, setCsvFields] = useState<CsvSourcingField[]>([]);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvReading, setCsvReading] = useState(false);
  const [csvNotice, setCsvNotice] = useState<string | null>(null);
  const [sourcingParams, setSourcingParams] = useState<SourcingParams>(
    sourcingParamsFromApolloInput(apolloSearchInput),
  );
  // People the operator removed from the sourcing preview. Persisted on the
  // draft (so it survives a remount AND reaches campaign-prepare, which excludes
  // them at launch) and folded into every search's exclude set so a removed
  // person never re-appears on a refresh or a "load more".
  const [removedApolloPersonIds, setRemovedApolloPersonIds] = useState<string[]>(
    readRemovedApolloPersonIds(apolloSearchInput),
  );
  // Candidates fetched by "load more" (page 2+); page 1 comes from the useQuery
  // below. Reset whenever a new search is committed.
  const [extraSourcedLeads, setExtraSourcedLeads] = useState<LeadRecord[]>([]);
  const [sourcedPagesLoaded, setSourcedPagesLoaded] = useState(1);
  const [loadingMoreLeads, setLoadingMoreLeads] = useState(false);
  const [sourcingExhausted, setSourcingExhausted] = useState(false);
  // Apollo search is only fired when the user clicks "Refresh" — we commit the
  // current sourcing form into an input value, and useQuery refetches whenever
  // that committed reference changes.
  const [committedSearch, setCommittedSearch] = useState<ApolloLeadSearchInput | null>(
    apolloSearchInput
      ? apolloLeadSearchInput(apolloSearchPayload(sourcingParamsFromApolloInput(apolloSearchInput)), {
          excludeApolloPersonIds: readRemovedApolloPersonIds(apolloSearchInput),
        })
      : null,
  );

  // HubSpot list import state
  const [hubspotAvailable, setHubspotAvailable] = useState(false);
  const [hubspotLists, setHubspotLists] = useState<Array<{ listId: string; name: string; size?: number }> | null>(null);
  const [hubspotLoadingLists, setHubspotLoadingLists] = useState(false);
  const [hubspotImporting, setHubspotImporting] = useState<string | null>(null);
  const [hubspotError, setHubspotError] = useState<string | null>(null);
  const [hubspotDialogOpen, setHubspotDialogOpen] = useState(false);
  const [lastImportSummary, setLastImportSummary] = useState<string | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  const [detailModalLead, setDetailModalLead] = useState<any | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, any>>({});
  // Signature of the last ICP interpretation that was written into the form
  // fields — prevents re-filling fields the user has cleared on purpose.
  const [appliedInterpretationSig, setAppliedInterpretationSig] = useState<string | null>(null);
  // Snapshot of the values the interpretation effect itself last wrote per
  // field. Lets a later interpretation tell "still exactly what we auto-filled
  // — safe to replace" apart from "the user has since typed/picked something
  // else — leave it alone", instead of just checking emptiness (which gets a
  // field permanently stuck on the FIRST interpretation forever after).
  // Persisted inside the draft payload so it survives leaving/re-entering the
  // wizard — as plain component state it reset to {} on every remount and all
  // previously auto-filled values were suddenly treated as user input.
  const [autoFilled, setAutoFilled] = useState<Partial<SourcingParams>>(autoFilledFromApolloInput(apolloSearchInput));
  const [csvExporting, setCsvExporting] = useState(false);

  // Probe CRM config once on mount-ish (stateful re-runs are fine).
  void (async () => {
    if (hubspotAvailable) return;
    try {
      const cfg = await getCrmConfig()({ hubspot: { enabled: 1 } });
      if (cfg?.hubspot?.enabled) setHubspotAvailable(true);
    } catch {
      /* ignore */
    }
  })();

  const openHubspotDialog = async () => {
    setHubspotError(null);
    setHubspotDialogOpen(true);
    if (hubspotLists !== null) return;
    setHubspotLoadingLists(true);
    try {
      const r = await fetch('/api/crm/hubspot/lists').then(x => x.json());
      if (!r?.ok) throw new Error(r?.error ?? 'Failed to load lists');
      setHubspotLists(r.lists ?? []);
    } catch (e: any) {
      setHubspotError(e?.message ?? 'Failed to load HubSpot lists');
      setHubspotLists([]);
    } finally {
      setHubspotLoadingLists(false);
    }
  };

  /**
   * Merge `incoming` into the current `leads` array, deduplicating by email.
   * Incoming entries override existing ones (so re-importing a HubSpot list
   * refreshes its rows; a CSV row for a HubSpot-imported email upgrades the
   * row but preserves the existing `crmContactId` if the new row lacks one).
   */
  const mergeLeads = (incoming: LeadRecord[]): { merged: LeadRecord[]; added: number; updated: number } => {
    // Deduplicate by email when present, otherwise by LinkedIn URL, so a
    // LinkedIn-only lead (no email) is kept instead of being dropped. Leads
    // with neither are not contactable and are skipped.
    const dedupKey = (l: LeadRecord): string | null => {
      const email = l.email?.trim().toLowerCase();
      if (email) return `email:${email}`;
      const li = l.linkedinUrl?.trim().toLowerCase();
      if (li) return `li:${li}`;
      return null;
    };
    const byKey = new Map<string, LeadRecord>();
    for (const l of leads) {
      const key = dedupKey(l);
      if (key) byKey.set(key, l);
    }
    let added = 0;
    let updated = 0;
    for (const inc of incoming) {
      const key = dedupKey(inc);
      if (!key) continue;
      const prev = byKey.get(key);
      if (prev) {
        // Preserve existing crmContactId/source when the incoming row lacks one.
        byKey.set(key, {
          ...prev,
          ...inc,
          crmContactId: inc.crmContactId ?? prev.crmContactId,
          source: inc.source ?? prev.source,
        });
        updated++;
      } else {
        byKey.set(key, inc);
        added++;
      }
    }
    return { merged: Array.from(byKey.values()), added, updated };
  };

  const importHubspotList = async (listId: string, name: string) => {
    setHubspotImporting(listId);
    setHubspotError(null);
    try {
      const r = await fetch(`/api/crm/hubspot/lists/${encodeURIComponent(listId)}/contacts?max=2000`).then(x =>
        x.json(),
      );
      if (!r?.ok) throw new Error(r?.error ?? 'Failed to import contacts');
      const imported: LeadRecord[] = (r.contacts ?? [])
        .filter((c: any) => c.email)
        .map((c: any) => ({
          firstName: c.firstName ?? '',
          lastName: c.lastName ?? '',
          email: c.email,
          company: c.company ?? '',
          jobTitle: c.jobTitle ?? '',
          linkedinUrl: c.linkedinUrl,
          industry: c.industry,
          crmContactId: c.crmContactId,
          source: 'hubspot' as const,
        }));
      if (imported.length === 0) throw new Error('List contained no contacts with an email address');
      const { merged, added, updated } = mergeLeads(imported);
      setFileName(leads.length === 0 ? `HubSpot · ${name}` : `${leads.length} existing + HubSpot · ${name}`);
      onLeadsChange(merged);
      setHubspotDialogOpen(false);
      setParseError(null);
      // Surface a quick summary in the success area; reuse the same banner.
      setLastImportSummary(`Imported ${imported.length} from HubSpot · ${name} (↗ ${added} new, ↻ ${updated} updated)`);
    } catch (e: any) {
      setHubspotError(e?.message ?? 'Import failed');
    } finally {
      setHubspotImporting(null);
    }
  };

  /** Process a selected/dropped file (CSV or Excel). */
  const processFile = async (file: File) => {
    setParseError(null);
    const name = file.name.toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    const isCsv = name.endsWith('.csv') || name.endsWith('.tsv') || name.endsWith('.txt');
    if (!isCsv && !isExcel) {
      setParseError('Unsupported file type. Please upload a .csv or .xlsx file.');
      return;
    }
    try {
      let rows: string[][];
      if (isExcel) {
        const buffer = await readFileAsArrayBuffer(file);
        rows = parseExcelFile(buffer);
      } else {
        const text = await readFileAsText(file);
        rows = parseCsvText(text);
      }
      const { leads: parsed, errors } = csvRowsToLeads(rows);
      if (errors.length > 0) {
        setParseError(errors.join('\n'));
        return;
      }
      const tagged = parsed.map(p => ({ ...p, source: 'csv' as const }));
      const { merged, added, updated } = mergeLeads(tagged);
      setFileName(leads.length === 0 ? file.name : `${leads.length} existing + ${file.name}`);
      onLeadsChange(merged);
      setLastImportSummary(`Imported ${tagged.length} from ${file.name} (↗ ${added} new, ↻ ${updated} updated)`);
    } catch {
      setParseError('Failed to read file. Make sure it is a valid CSV or Excel file.');
    }
  };

  /**
   * Read a file uploaded to STEER the sourcing rather than to import leads.
   * Unlike `processFile` it wants no email column: it keeps every column, asks
   * the server which sourcing dimension each one means, and leaves the values
   * for the operator to prune before anything is searched.
   */
  const processSourcingFile = async (file: File) => {
    setCsvNotice(null);
    const name = file.name.toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    const isCsv = name.endsWith('.csv') || name.endsWith('.tsv') || name.endsWith('.txt');
    if (!isCsv && !isExcel) {
      setCsvNotice('Unsupported file type. Please upload a .csv or .xlsx file.');
      return;
    }
    setCsvReading(true);
    try {
      const rows = isExcel
        ? parseExcelFile(await readFileAsArrayBuffer(file))
        : parseCsvText(await readFileAsText(file));
      const columns = readCsvColumns(rows);
      if (columns.length === 0) {
        setCsvNotice('That file has no readable columns.');
        setCsvReading(false);
        return;
      }
      let classifications: unknown = [];
      let failed = false;
      try {
        const resp = await fetch('/api/sourcing/classify-csv-columns', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ columns: describeColumnsForClassifier(columns) }),
        });
        const data = (await resp.json()) as { ok?: boolean; classifications?: unknown; failed?: boolean };
        classifications = data.classifications ?? [];
        failed = data.failed === true || data.ok !== true;
      } catch {
        failed = true;
      }
      const { fields, unknownSlots } = normalizeClassifications(columns, classifications);
      setCsvFields(fields);
      setCsvFileName(file.name);
      if (failed) {
        // Never a code-side guess from the header — the operator assigns the
        // columns by hand instead, which is slower but never silently wrong.
        setCsvNotice('Could not read the columns automatically. Pick a field for the ones you want to use.');
      } else if (unknownSlots.length > 0) {
        console.warn('[sourcing-csv] classifier returned unknown field types', unknownSlots);
        setCsvNotice(`Ignored ${unknownSlots.length} unrecognised field type(s) from the automatic reading.`);
      }
    } catch {
      setCsvNotice('Failed to read that file. Make sure it is a valid CSV or Excel file.');
    }
    setCsvReading(false);
  };

  const pickSourcingFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.tsv,.txt,.xlsx,.xls';
    input.style.display = 'none';
    const cleanup = () => input.remove();
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      cleanup();
      if (file) void processSourcingFile(file);
    });
    document.body.appendChild(input);
    input.click();
  };

  /** Re-assign one column to a different sourcing dimension (or to none). */
  const setCsvFieldSlot = (index: number, slot: string) => {
    setCsvFields(csvFields.map(f => (f.index === index ? { ...f, slot: slot as CsvSourcingField['slot'] } : f)));
  };

  /** Drop one value from a column. Emptying a column un-constrains it entirely. */
  const removeCsvFieldValue = (index: number, value: string) => {
    setCsvFields(csvFields.map(f => (f.index === index ? { ...f, selected: f.selected.filter(v => v !== value) } : f)));
  };

  const clearSourcingFile = () => {
    setCsvFields([]);
    setCsvFileName(null);
    setCsvNotice(null);
  };

  /**
   * Fold what survived pruning into the sourcing form. UNION, never replace —
   * see mergeCsvOverlay. From here on the values live in `sourcingParams`, so
   * they persist with the draft and launch sources with exactly what is shown.
   */
  const applySourcingFile = () => {
    const next = mergeCsvOverlay(sourcingParams, csvSourcingOverlay(csvFields));
    setSourcingParams(next);
    // `next` is passed explicitly — see refreshSourcingPreview. Reading the
    // state back here would preview the search from BEFORE the file was applied.
    refreshSourcingPreview(next);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files[0];
    if (file) processFile(file);
  };

  const handleFileSelect = () => {
    // The input must be attached to the DOM so the `change` event fires
    // reliably across browsers — a detached input can be garbage-collected
    // before the user picks a file, so nothing would happen.
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.tsv,.txt,.xlsx,.xls';
    input.style.display = 'none';
    const cleanup = () => input.remove();
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      cleanup();
      if (file) void processFile(file);
    });
    // Remove the element again if the user cancels the picker (modern browsers).
    input.addEventListener('cancel', cleanup);
    document.body.appendChild(input);
    input.click();
  };

  const clearLeads = () => {
    setFileName(null);
    setParseError(null);
    setLastImportSummary(null);
    onLeadsChange([]);
  };

  /**
   * Persist the sourcing form as the campaign draft. The auto-fill snapshot
   * (`autoFilled`) and the preview curation (`removedApolloPersonIds`) ride
   * along inside the payload so both a wizard remount AND the server-side
   * campaign-prepare sourcing can honour them — prepare excludes the removed
   * people at launch. The backend search input builder ignores both extra keys.
   */
  const persistDraft = (
    params: SourcingParams,
    auto: Partial<SourcingParams>,
    removed: string[] = removedApolloPersonIds,
  ) => {
    onApolloSearchInputChange?.({ ...apolloSearchPayload(params), autoFilled: auto, removedApolloPersonIds: removed });
  };

  const setSourcingParam = (key: keyof SourcingParams, value: string | string[] | number | null | undefined) => {
    const next = { ...sourcingParams, [key]: value };
    setSourcingParams(next);
    persistDraft(next, autoFilled);
  };

  const apolloQuery = useQuery(
    () =>
      committedSearch
        ? searchApolloLeads(committedSearch)({
            totalEntries: 1,
            appliedFilters: {
              personTitles: 1,
              personSeniorities: 1,
              personLocations: 1,
              organizationLocations: 1,
              organizationNames: 1,
              organizationIndustryKeywords: 1,
              employeeRanges: 1,
              departments: 1,
              revenueMinUsd: 1,
              revenueMaxUsd: 1,
              qKeywords: 1,
              mappingFailed: 1,
            },
            leads: {
              apolloPersonId: 1,
              firstName: 1,
              lastName: 1,
              email: 1,
              company: 1,
              jobTitle: 1,
              linkedinUrl: 1,
              industry: 1,
              employeeCount: 1,
              lastRefreshedAt: 1,
              hasEmail: 1,
              hasDirectPhone: 1,
            },
          })
        : Promise.resolve(null),
    [committedSearch],
  );

  const apolloLeads: LeadRecord[] = (apolloQuery.value?.leads ?? []).map(lead => ({
    firstName: lead.firstName,
    lastName: lead.lastName,
    email: lead.email,
    company: lead.company,
    jobTitle: lead.jobTitle,
    linkedinUrl: lead.linkedinUrl,
    industry: lead.industry,
    employeeCount: lead.employeeCount,
    apolloPersonId: lead.apolloPersonId,
    hasEmail: lead.hasEmail,
    hasDirectPhone: lead.hasDirectPhone,
    source: 'eigen',
  }));

  const sourcingPreview = {
    loading: committedSearch !== null && apolloQuery.loading,
    error: apolloQuery.error ? apolloQuery.error.message : null,
    leads: apolloQuery.value ? apolloLeads : [],
    totalEntries: apolloQuery.value?.totalEntries ?? null,
  };

  // The list the operator curates: page 1 (reactive to the committed search)
  // plus everything "Load more" appended, with removed people dropped and any
  // duplicate apolloPersonId collapsed.
  const curatedLeads = curatedSourcedLeads(sourcingPreview.leads, extraSourcedLeads, removedApolloPersonIds);

  // A newly committed search invalidates the appended pages — start the
  // "load more" accumulation over.
  useEffect(() => {
    setExtraSourcedLeads([]);
    setSourcedPagesLoaded(1);
    setSourcingExhausted(false);
  }, [committedSearch]);

  const removeSourcedLead = (apolloPersonId: string | undefined) => {
    if (!apolloPersonId || removedApolloPersonIds.includes(apolloPersonId)) return;
    const next = [...removedApolloPersonIds, apolloPersonId];
    setRemovedApolloPersonIds(next);
    // Persist immediately so the removal binds even if the operator launches
    // without touching the form again.
    persistDraft(sourcingParams, autoFilled, next);
  };

  const restoreRemovedLeads = () => {
    if (removedApolloPersonIds.length === 0) return;
    setRemovedApolloPersonIds([]);
    persistDraft(sourcingParams, autoFilled, []);
  };

  const loadMoreSourcedLeads = async () => {
    if (loadingMoreLeads || !committedSearch) return;
    setLoadingMoreLeads(true);
    try {
      const nextPage = sourcedPagesLoaded + 1;
      const input = apolloLeadSearchInput(
        { ...withoutStaleAutoFill(sourcingParams), page: nextPage, perPage: PREVIEW_PAGE_SIZE },
        { excludeApolloPersonIds: unionExcludeIds(excludeApolloPersonIds, removedApolloPersonIds) },
      );
      const result = await searchApolloLeads(input)({
        leads: {
          apolloPersonId: 1,
          firstName: 1,
          lastName: 1,
          email: 1,
          company: 1,
          jobTitle: 1,
          linkedinUrl: 1,
          industry: 1,
          employeeCount: 1,
          hasEmail: 1,
          hasDirectPhone: 1,
        },
      });
      const already = new Set(fetchedApolloPersonIds(sourcingPreview.leads, extraSourcedLeads));
      const fresh: LeadRecord[] = (result.leads ?? [])
        .filter(lead => !!lead.apolloPersonId && !already.has(lead.apolloPersonId))
        .map(lead => ({
          firstName: lead.firstName,
          lastName: lead.lastName,
          email: lead.email,
          company: lead.company,
          jobTitle: lead.jobTitle,
          linkedinUrl: lead.linkedinUrl,
          industry: lead.industry,
          employeeCount: lead.employeeCount,
          apolloPersonId: lead.apolloPersonId,
          hasEmail: lead.hasEmail,
          hasDirectPhone: lead.hasDirectPhone,
          source: 'eigen',
        }));
      setSourcedPagesLoaded(nextPage);
      if (fresh.length === 0) setSourcingExhausted(true);
      else setExtraSourcedLeads([...extraSourcedLeads, ...fresh]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load more leads.');
    } finally {
      setLoadingMoreLeads(false);
    }
  };

  const fetchLeadDetail = async (apolloPersonId: string) => {
    const cached = detailCache[apolloPersonId];
    if (cached) {
      setDetailModalLead(cached);
      return;
    }
    setDetailLoadingId(apolloPersonId);
    try {
      const result = await getApolloLeadDetail(apolloPersonId)({
        firstName: 1,
        lastName: 1,
        email: 1,
        emailStatus: 1,
        phone: 1,
        linkedinUrl: 1,
        jobTitle: 1,
        company: 1,
        industry: 1,
        city: 1,
        state: 1,
        country: 1,
      });
      const nextCache = { ...detailCache, [apolloPersonId]: result };
      setDetailCache(nextCache);
      setDetailModalLead(result);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to load lead details');
    } finally {
      setDetailLoadingId(null);
    }
  };

  /**
   * Fields that still hold exactly what a PRIOR ICP interpretation auto-filled
   * them with (the user never edited them by hand) must NOT be unioned into
   * the NEXT interpretation's request — Apollo ORs multiple values within the
   * same filter, so re-describing the ICP with a different location/industry/
   * etc. would otherwise keep searching the OLD one too (tester: "CEOs of
   * Indian manufacturing companies" kept returning Dutch leads because the
   * Company HQ Location field was still carrying "Netherlands" from the
   * previous ICP). Cleared fields fall back to the fresh mapping server-side.
   */
  const withoutStaleAutoFill = (params: SourcingParams): SourcingParams => {
    const isStale = <K extends keyof SourcingParams>(key: K): boolean =>
      JSON.stringify(params[key]) === JSON.stringify(autoFilled[key]);
    const cleaned = { ...params };
    if (isStale('companyLocation')) cleaned.companyLocation = [];
    if (isStale('personLocation')) cleaned.personLocation = [];
    if (isStale('companyIndustry')) cleaned.companyIndustry = [];
    if (isStale('industryKeywords')) cleaned.industryKeywords = '';
    if (isStale('department')) cleaned.department = [];
    if (isStale('employeeCount')) cleaned.employeeCount = [];
    if (isStale('jobTitle')) cleaned.jobTitle = '';
    if (isStale('revenueMin')) cleaned.revenueMin = null;
    if (isStale('revenueMax')) cleaned.revenueMax = null;
    return cleaned;
  };

  /**
   * Commit the current parameters as the preview search. `params` is explicit so
   * a caller that has just produced a NEW set can pass it directly: the state
   * setter has not landed yet in the same tick, so reading `sourcingParams` from
   * the closure would search with the pre-change values.
   */
  const refreshSourcingPreview = (params: SourcingParams = sourcingParams) => {
    persistDraft(params, autoFilled);
    setCommittedSearch(
      apolloLeadSearchInput(apolloSearchPayload(withoutStaleAutoFill(params)), {
        excludeApolloPersonIds: unionExcludeIds(excludeApolloPersonIds, removedApolloPersonIds),
      }),
    );
  };

  /**
   * Bypass the persisted ICP→Apollo mapping and ask the LLM for a fresh
   * interpretation (which then becomes the new deterministic baseline).
   * For users who are unhappy with the current interpretation.
   *
   * The Plain English ICP WINS and OVERWRITES (UAT). Unlike the ordinary
   * refresh — which preserves a hand-edited detail field — an explicit
   * reinterpret regenerates the detail fields from the ICP text wholesale. We
   * clear every ICP-controlled field first, so the fresh interpretation drives
   * BOTH the search (empty fields → the server maps purely from the text,
   * nothing stale unioned in) and the visible form (the apply-effect refills a
   * field only while it is empty or still exactly what a prior interpretation
   * wrote). A hand-added "USA" on an ICP that says "Europe" no longer survives.
   *
   * `autoFilled` is reset in step, and `appliedInterpretationSig` is cleared so
   * the fresh interpretation re-applies even when it yields the same signature
   * as the last one (same ICP text, forced remap) — without that, the dedup
   * guard would leave the just-cleared fields empty.
   */
  const reinterpretIcp = () => {
    const cleared = clearIcpControlledFields(sourcingParams);
    setSourcingParams(cleared);
    setAutoFilled({});
    setAppliedInterpretationSig(null);
    persistDraft(cleared, {});
    setCommittedSearch(
      apolloLeadSearchInput(apolloSearchPayload(cleared), {
        forceRemap: true,
        excludeApolloPersonIds: unionExcludeIds(excludeApolloPersonIds, removedApolloPersonIds),
      }),
    );
  };

  /**
   * Downloads a CSV of the sourced leads — exactly the "Lead count" the user
   * configured (e.g. 200), not just the on-screen preview sample. Runs a fresh
   * search at that full count rather than reusing the preview, which only holds
   * the pages the operator has loaded.
   */
  const downloadSourcedLeadsCsv = async () => {
    if (csvExporting) return;
    setCsvExporting(true);
    try {
      const limit = normalizeLeadLimit(sourcingParams.leadLimit);
      const input = apolloLeadSearchInput(
        { ...withoutStaleAutoFill(sourcingParams), page: 1, perPage: Number.parseInt(limit, 10) },
        // Match the curation: the export is what WOULD be sourced, so it excludes
        // removed people exactly as campaign-prepare will at launch.
        { excludeApolloPersonIds: unionExcludeIds(excludeApolloPersonIds, removedApolloPersonIds) },
      );
      const result = await searchApolloLeads(input)({
        leads: {
          apolloPersonId: 1,
          firstName: 1,
          lastName: 1,
          email: 1,
          company: 1,
          jobTitle: 1,
          linkedinUrl: 1,
          industry: 1,
          employeeCount: 1,
        },
      });
      const previews = result.leads ?? [];
      if (previews.length === 0) {
        toast.error('No leads found to export.');
        return;
      }

      // Enrich for real name/email/LinkedIn — the search preview above only
      // has obfuscated names and no LinkedIn URL at all. Costs ~1 Apollo
      // credit per matched lead; a failure here still exports the preview
      // data instead of losing the whole download.
      const enrichedById = new Map<
        string,
        {
          firstName?: string;
          lastName?: string;
          email?: string;
          linkedinUrl?: string;
          jobTitle?: string;
          company?: string;
          industry?: string;
        }
      >();
      try {
        const enriched = await enrichApolloLeadsForExport(previews.map(l => l.apolloPersonId))({
          apolloPersonId: 1,
          firstName: 1,
          lastName: 1,
          email: 1,
          linkedinUrl: 1,
          jobTitle: 1,
          company: 1,
          industry: 1,
        });
        for (const e of enriched) enrichedById.set(e.apolloPersonId, e);
      } catch (err: any) {
        toast.error(err?.message ?? 'Lead enrichment failed — exporting with preview data only');
      }

      const rows: LeadRecord[] = previews.map(lead => {
        const enriched = enrichedById.get(lead.apolloPersonId);
        return {
          firstName: enriched?.firstName || lead.firstName,
          lastName: enriched?.lastName || lead.lastName,
          email: enriched?.email ?? lead.email,
          company: enriched?.company || lead.company,
          jobTitle: enriched?.jobTitle || lead.jobTitle,
          linkedinUrl: enriched?.linkedinUrl ?? lead.linkedinUrl,
          industry: enriched?.industry ?? lead.industry,
          employeeCount: lead.employeeCount,
        };
      });
      const nameSlug = (campaignName || 'leads')
        .trim()
        .replace(/[^\w-]+/g, '_')
        .toLowerCase();
      downloadTextFile(`${nameSlug}_leads.csv`, leadsToCsv(rows), 'text/csv;charset=utf-8;');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to export leads to CSV');
    } finally {
      setCsvExporting(false);
    }
  };

  /** Read the (optional) appliedFilters result defensively. */
  const readAppliedFilters = () => {
    const af = (() => {
      try {
        return apolloQuery.value?.appliedFilters;
      } catch {
        return undefined;
      }
    })();
    if (!af) return null;
    const read = (get: () => string[] | undefined): string[] => {
      try {
        return get() ?? [];
      } catch {
        return [];
      }
    };
    const readNumber = (get: () => number | undefined): number | null => {
      try {
        const v = get();
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
      } catch {
        return null;
      }
    };
    const readBool = (get: () => boolean | undefined): boolean => {
      try {
        return get() === true;
      } catch {
        return false;
      }
    };
    return {
      personTitles: read(() => af.personTitles),
      personSeniorities: read(() => af.personSeniorities),
      personLocations: read(() => af.personLocations),
      organizationLocations: read(() => af.organizationLocations),
      industries: read(() => af.organizationIndustryKeywords),
      employeeRanges: read(() => af.employeeRanges),
      departments: read(() => af.departments),
      revenueMinUsd: readNumber(() => af.revenueMinUsd),
      revenueMaxUsd: readNumber(() => af.revenueMaxUsd),
      mappingFailed: readBool(() => af.mappingFailed),
    };
  };

  // Canonical lookups so interpreted values land as proper dropdown entries.
  const countryByLower = new Map(ALL_COUNTRY_NAMES.map(c => [c.toLowerCase(), c]));
  // Common shorthands / bare names for the abbreviation-suffixed entries.
  countryByLower.set('united kingdom', 'United Kingdom (UK)');
  countryByLower.set('uk', 'United Kingdom (UK)');
  countryByLower.set('great britain', 'United Kingdom (UK)');
  countryByLower.set('united states', 'United States (USA)');
  countryByLower.set('united states of america', 'United States (USA)');
  countryByLower.set('usa', 'United States (USA)');
  const industryByLower = new Map(COMPANY_INDUSTRIES.map(i => [i.toLowerCase(), i]));

  /**
   * Map a place string to the value we put into the location fields. Country/
   * region names are canonicalized to their dropdown entry; city-level places
   * ("Dublin, Ireland") are kept VERBATIM — Apollo accepts city strings, and
   * resolving them to the whole country would silently widen the search
   * (tester: "near Dublin" must not become all of Ireland).
   */
  const placeToFieldValue = (raw: string): string => {
    const direct = countryByLower.get(raw.trim().toLowerCase());
    return direct ?? raw.trim();
  };

  // Write the ICP interpretation into the structured form fields (steps 2+3)
  // instead of piling everything into the preview column. A field is (re)filled
  // whenever it's empty OR still holds exactly what a PRIOR interpretation
  // auto-filled it with; the moment the user edits a field by hand it stops
  // tracking further interpretations, so their edit sticks — even across a
  // later ICP rewrite (tester: re-describing "Dutch" as "Indian manufacturing
  // companies" left the Company HQ Location field, and therefore the search,
  // stuck on Netherlands, since the old code only ever filled EMPTY fields).
  useEffect(() => {
    const af = readAppliedFilters();
    if (!af) return;
    const sig = JSON.stringify(af);
    if (sig === appliedInterpretationSig) return;

    const next: SourcingParams = { ...sourcingParams };
    const nextAuto: Partial<SourcingParams> = { ...autoFilled };
    let changed = false;

    const sameArray = (a: string[] | undefined, b: string[]): boolean =>
      !!a && a.length === b.length && a.every((v, i) => v === b[i]);
    // When the NEW interpretation has no value for a field, a value WE
    // auto-filled earlier is stale and must be cleared — leaving it standing
    // showed the previous ICP's location/industry as if it still applied and
    // made users doubt their Refresh did anything. Hand-edited values stay.
    const fillArray = (
      key: 'companyLocation' | 'personLocation' | 'companyIndustry' | 'department' | 'employeeCount',
      values: string[],
    ) => {
      const current = next[key];
      const wasAutoFilled = sameArray(autoFilled[key], current);
      if (values.length === 0) {
        if (wasAutoFilled && current.length > 0) {
          next[key] = [];
          delete nextAuto[key];
          changed = true;
        }
        return;
      }
      const untouched = current.length === 0 || wasAutoFilled;
      if (!untouched) return;
      next[key] = values;
      nextAuto[key] = values;
      changed = true;
    };
    const fillString = (key: 'jobTitle' | 'industryKeywords', value: string) => {
      const current = next[key];
      const wasAutoFilled = autoFilled[key] === current;
      if (!value) {
        if (wasAutoFilled && current.trim()) {
          next[key] = '';
          delete nextAuto[key];
          changed = true;
        }
        return;
      }
      const untouched = !current.trim() || wasAutoFilled;
      if (!untouched) return;
      next[key] = value;
      nextAuto[key] = value;
      changed = true;
    };
    const fillNumber = (key: 'revenueMin' | 'revenueMax', value: number | null) => {
      const current = next[key];
      const wasAutoFilled = autoFilled[key] === current;
      if (value === null) {
        if (wasAutoFilled && current !== null) {
          next[key] = null;
          delete nextAuto[key];
          changed = true;
        }
        return;
      }
      const untouched = current === null || wasAutoFilled;
      if (!untouched) return;
      next[key] = value;
      nextAuto[key] = value;
      changed = true;
    };

    fillString('jobTitle', af.personTitles.join(', '));

    const hqPlaces = [...new Set(af.organizationLocations.map(placeToFieldValue).filter(Boolean))];
    fillArray('companyLocation', hqPlaces);

    // Person location: use the mapper's person places; for company-type ICPs
    // (person places empty) mirror the company places so the CONTACTS are
    // also filtered to the region — without this, a Dublin company's exec
    // sitting in Strasbourg still matched. Editable/removable like any field.
    const personPlaces = [
      ...new Set(
        (af.personLocations.length > 0 ? af.personLocations : af.organizationLocations)
          .map(placeToFieldValue)
          .filter(Boolean),
      ),
    ];
    fillArray('personLocation', personPlaces);

    const canonicalIndustries = [
      ...new Set(af.industries.map(i => industryByLower.get(i.trim().toLowerCase())).filter((c): c is string => !!c)),
    ];
    fillArray('companyIndustry', canonicalIndustries);

    const leftoverIndustries = af.industries.filter(i => !industryByLower.has(i.trim().toLowerCase()));
    fillString('industryKeywords', leftoverIndustries.join(', '));

    // Server already canonicalizes departments against the DEPARTMENTS enum.
    fillArray('department', [...new Set(af.departments)]);

    // Employee count: fill the field with the EXACT interpreted range (e.g.
    // "100-5000") instead of approximating it with the fixed buckets — testers
    // read "201-500" as "the mapping starts at 201" when their ICP said 100.
    // The server parses "min-max" and "min+" chips exactly like bucket values.
    const employeeChips = af.employeeRanges
      .map(r => {
        const m = r.match(/^(\d+),(\d+)$/);
        if (!m) return null;
        const lo = Number(m[1]);
        const hi = Number(m[2]);
        return hi >= 1000000 ? `${lo}+` : `${lo}-${hi}`;
      })
      .filter((c): c is string => c !== null);
    fillArray('employeeCount', [...new Set(employeeChips)]);

    fillNumber('revenueMin', af.revenueMinUsd !== null ? Math.round(af.revenueMinUsd / 1_000_000) : null);
    fillNumber('revenueMax', af.revenueMaxUsd !== null ? Math.round(af.revenueMaxUsd / 1_000_000) : null);

    setAppliedInterpretationSig(sig);
    if (changed) {
      setSourcingParams(next);
      setAutoFilled(nextAuto);
      persistDraft(next, nextAuto);
    }
  }, [apolloQuery.value]);

  /**
   * Chip groups for interpretation details without a form field. Since places
   * are now written verbatim into the location fields (incl. city-level
   * strings), nothing is left to show here — kept as an extension point.
   */
  const appliedFilterGroups = (): { label: string; values: string[] }[] => [];

  const stats = () => {
    if (leads.length === 0) return nothing;
    const withLinkedin = leads.filter(l => l.linkedinUrl).length;
    const withEmail = leads.filter(l => l.email).length;
    const companies = new Set(leads.map(l => l.company)).size;
    return html`
      <div class="grid grid-cols-3 gap-3">
        <div class="bg-primary/10 rounded-lg p-3 text-center">
          <div class="text-primary text-2xl font-bold">${leads.length}</div>
          <div class="text-base-content/60 text-xs">Total Leads</div>
        </div>
        <div class="bg-secondary/10 rounded-lg p-3 text-center">
          <div class="text-secondary text-2xl font-bold">${companies}</div>
          <div class="text-base-content/60 text-xs">Companies</div>
        </div>
        <div class="bg-info/10 rounded-lg p-3 text-center">
          <div class="text-info text-2xl font-bold">${withLinkedin}</div>
          <div class="text-base-content/60 text-xs">With LinkedIn</div>
        </div>
      </div>
      ${channelCoverageBanner(withLinkedin, withEmail)}
    `;
  };

  /**
   * Channel-coverage warning. The backend skips ineligible leads silently
   * during message generation — we surface that here so the user can fix the
   * import (add LinkedIn URLs, switch campaign type, …) before paying for
   * LLM calls that produce nothing.
   */
  const channelCoverageBanner = (withLinkedin: number, withEmail: number) => {
    if (!campaignType || leads.length === 0) return nothing;
    const total = leads.length;
    if (campaignType === 'linkedin') {
      const missing = total - withLinkedin;
      if (missing === 0) return nothing;
      return html`
        <div class="alert alert-warning text-sm">
          <span class="icon-[material-symbols--warning-outline] text-lg"></span>
          <span>
            <strong>${missing}</strong> of ${total} leads have no LinkedIn URL and will be <strong>skipped</strong> in
            this LinkedIn-only campaign — no messages will be generated for them. Add a LinkedIn URL column to your
            import or switch to a multi-channel campaign to also reach them by email.
          </span>
        </div>
      `;
    }
    if (campaignType === 'email') {
      const missing = total - withEmail;
      if (missing === 0) return nothing;
      return html`
        <div class="alert alert-warning text-sm">
          <span class="icon-[material-symbols--warning-outline] text-lg"></span>
          <span>
            <strong>${missing}</strong> of ${total} leads have no email address and will be <strong>skipped</strong> in
            this email-only campaign.
          </span>
        </div>
      `;
    }
    // multi: info banner only when partial coverage exists.
    const liOnly = withLinkedin;
    const emailOnly = withEmail;
    const fullyCovered = leads.filter(l => l.email && l.linkedinUrl).length;
    const noChannel = leads.filter(l => !l.email && !l.linkedinUrl).length;
    if (liOnly === total && emailOnly === total) return nothing;
    return html`
      <div class="alert alert-info text-sm">
        <span class="icon-[material-symbols--info-outline] text-lg"></span>
        <span>
          Channel coverage — <strong>${fullyCovered}</strong> leads have both channels,
          <strong>${emailOnly - fullyCovered}</strong> email-only,
          <strong>${liOnly - fullyCovered}</strong> LinkedIn-only.
          ${
            noChannel > 0
              ? html`<strong class="text-warning"
                >${noChannel} lead${noChannel === 1 ? ' has' : 's have'} no channel at all and will be skipped.</strong
              >`
              : nothing
          }
          Multi-channel campaigns send each step on the channel the lead supports.
        </span>
      </div>
    `;
  };

  return html`
    <div class="flex flex-col gap-6">
      <!-- Campaign Name -->
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body flex flex-col gap-2">
          <div class="flex items-center gap-3">
            <span class="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--badge] text-xl"></span>
            </span>
            <div>
              <h2 class="card-title">Campaign Name <span class="text-error" title="Required">*</span></h2>
              <p class="text-base-content/70 text-sm">
                Give this campaign a recognisable name. You can change it later.
              </p>
            </div>
          </div>
          ${TextInput({
            value: campaignName,
            onPicked: value => onCampaignNameChange(value ?? ''),
            placeholder: 'e.g., Q2 Enterprise Outreach',
          })}
          ${
            !campaignName.trim()
              ? html`<p class="text-warning flex items-center gap-1.5 text-sm">
                <span class="icon-[material-symbols--warning-outline] text-base"></span>
                Enter a campaign name to complete this step.
              </p>`
              : nothing
          }
        </div>
      </div>

      <!-- Project -->
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body flex flex-col gap-2">
          <div class="flex items-center gap-3">
            <span class="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--folder] text-xl"></span>
            </span>
            <div>
              <h2 class="card-title">Project</h2>
              <p class="text-base-content/70 text-sm">
                Campaigns in the same project never source or contact the same lead twice. Leave this on "Default
                Project" if that doesn't matter here — duplicates are allowed there.
              </p>
            </div>
          </div>
          ${
            creatingProject
              ? html`
                <div class="flex flex-col gap-2 md:flex-row md:items-end">
                  <div class="flex-1">
                    <span class="label-text mb-1 text-xs font-medium">Project Name</span>
                    ${TextInput({
                      value: newProjectName,
                      onPicked: value => setNewProjectName(value ?? ''),
                      placeholder: 'e.g., Gradias Project',
                    })}
                  </div>
                  <div class="flex-1">
                    <span class="label-text mb-1 text-xs font-medium">Description (optional)</span>
                    ${TextInput({
                      value: newProjectDescription,
                      onPicked: value => setNewProjectDescription(value ?? ''),
                      placeholder: 'What is this project about?',
                    })}
                  </div>
                  <div class="flex gap-2">
                    <button
                      type="button"
                      class="btn btn-primary btn-sm ${savingNewProject || !newProjectName.trim() ? 'btn-disabled' : ''}"
                      @click=${() => void createProject()}
                    >
                      ${savingNewProject ? html`<span class="loading loading-spinner loading-xs"></span>` : 'Create'}
                    </button>
                    <button type="button" class="btn btn-ghost btn-sm" @click=${() => setCreatingProject(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              `
              : html`
                <select
                  class="select select-bordered select-sm w-full max-w-sm"
                  @change=${(e: Event) => {
                    const value = (e.target as HTMLSelectElement).value;
                    if (value === '__new__') {
                      setCreatingProject(true);
                      return;
                    }
                    onProjectIdChange?.(value || undefined);
                  }}
                >
                  ${(projects ?? [])
                    .slice()
                    .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : a.name.localeCompare(b.name)))
                    .map(
                      p => html`
                        <option value=${p._id} ?selected=${projectId ? p._id === projectId : !!p.isDefault}>
                          ${p.isDefault ? 'Default Project' : p.name}
                        </option>
                      `,
                    )}
                  <option value="__new__">+ New Project…</option>
                </select>
              `
          }
        </div>
      </div>

      <!-- Upload Zone -->
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body flex flex-col gap-4">
          <div class="flex items-start gap-3">
            <span class="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--group] text-xl"></span>
            </span>
            <div>
              <h2 class="card-title">Choose Your Lead Source</h2>
              <p class="text-base-content/70 text-sm">
                Start with our sourcing, import from your CRM, or upload your own lead file.
              </p>
            </div>
          </div>

          <div class="grid gap-3 md:grid-cols-3">
            <button
              type="button"
              class="${
                selectedLeadSource === 'sourcing'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-base-300 hover:border-primary/50'
              } rounded-xl border p-4 text-left transition"
              @click=${() => setSelectedLeadSource('sourcing')}
            >
              <span class="icon-[material-symbols--manage-search] text-2xl"></span>
              <span class="mt-2 block font-semibold">Our sourcing</span>
              <span class="text-base-content/60 block text-xs">Define your ICP and let us find matching leads.</span>
            </button>
            <button
              type="button"
              class="${
                selectedLeadSource === 'hubspot'
                  ? 'border-warning bg-warning/5 text-warning'
                  : 'border-base-300 hover:border-warning/50'
              } rounded-xl border p-4 text-left transition"
              @click=${() => setSelectedLeadSource('hubspot')}
            >
              <span class="icon-[material-symbols--hub] text-2xl"></span>
              <span class="mt-2 block font-semibold">Use CRM</span>
              <span class="text-base-content/60 block text-xs">Import contacts from a HubSpot list.</span>
            </button>
            <button
              type="button"
              class="${
                selectedLeadSource === 'csv'
                  ? 'border-info bg-info/5 text-info'
                  : 'border-base-300 hover:border-info/50'
              } rounded-xl border p-4 text-left transition"
              @click=${() => setSelectedLeadSource('csv')}
            >
              <span class="icon-[material-symbols--upload-file] text-2xl"></span>
              <span class="mt-2 block font-semibold">Upload CSV</span>
              <span class="text-base-content/60 block text-xs">Upload a CSV or Excel file with your leads.</span>
            </button>
          </div>

          ${
            selectedLeadSource === 'sourcing'
              ? html`
                <div class="grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.85fr)]">
                  <div class="flex flex-col gap-4">
                    <div class="border-primary/20 bg-primary/5 rounded-xl border p-5">
                      <div class="mb-3 flex items-start gap-3">
                        <span
                          class="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                        >
                          <span class="icon-[material-symbols--psychology-alt] text-xl"></span>
                        </span>
                        <div>
                          <h3 class="flex items-center gap-1.5 font-semibold">
                            1. Plain English ICP
                            <span
                              class="icon-[material-symbols--info-outline] text-base-content/40 cursor-help text-base"
                              title="We combine what you write here with the filters below. If you mention a sector here and pick a different one below, we'll search for both."
                            ></span>
                          </h3>
                          <p class="text-base-content/70 text-sm">Describe who we should find in your own words.</p>
                        </div>
                      </div>
                      <textarea
                        class="textarea textarea-bordered min-h-28 w-full resize-y"
                        placeholder="e.g. CFOs and operations leaders at mid-market software companies in Europe that are scaling finance teams and care about automation."
                        .value=${sourcingParams.icpDescription}
                        @input=${(e: Event) =>
                          setSourcingParam('icpDescription', (e.target as HTMLTextAreaElement).value)}
                      ></textarea>
                    </div>

                    <div class="border-base-300 rounded-xl border p-5">
                      <div class="mb-4 flex items-start gap-3">
                        <span
                          class="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                        >
                          <span class="icon-[material-symbols--domain] text-xl"></span>
                        </span>
                        <div>
                          <h3 class="font-semibold">2. Target company</h3>
                          <p class="text-base-content/70 text-sm">Filter the accounts by firmographic criteria.</p>
                        </div>
                      </div>
                      <div class="grid gap-3 md:grid-cols-2">
                        <div class="form-control">
                          <span class="label-text mb-1 text-xs font-medium">Company HQ Location</span>
                          ${DropdownSelectionInput({
                            multi: true,
                            selectables: ALL_COUNTRY_NAMES,
                            value: sourcingParams.companyLocation,
                            onPicked: value => setSourcingParam('companyLocation', value),
                            placeholder: 'Search countries...',
                            emptyState: 'No countries found',
                          })}
                        </div>
                        <div class="form-control md:col-span-2">
                          <span class="label-text mb-1 text-xs font-medium">Company Industry</span>
                          ${DropdownSelectionInput({
                            multi: true,
                            selectables: COMPANY_INDUSTRIES,
                            value: sourcingParams.companyIndustry,
                            onPicked: value => setSourcingParam('companyIndustry', value),
                            placeholder: 'Select industries...',
                          })}
                        </div>
                        <div class="form-control md:col-span-2">
                          <span class="label-text mb-1 text-xs font-medium">Industry Keywords</span>
                          ${TextInput({
                            value: sourcingParams.industryKeywords,
                            onChanged: value => setSourcingParam('industryKeywords', value),
                            onPicked: value => setSourcingParam('industryKeywords', value),
                            placeholder: 'e.g. fintech, AI infrastructure, B2B SaaS',
                          })}
                        </div>
                        <div class="form-control">
                          <span class="label-text mb-1 text-xs font-medium">Employee Count</span>
                          ${DropdownSelectionInput({
                            multi: true,
                            selectables: EMPLOYEE_COUNTS,
                            value: sourcingParams.employeeCount,
                            onPicked: value => setSourcingParam('employeeCount', value),
                            placeholder: 'Select sizes...',
                          })}
                        </div>
                        <div class="form-control">
                          <span class="label-text mb-1 text-xs font-medium">Revenue (millions USD)</span>
                          <div class="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max="1000"
                              class="input input-bordered flex-1 text-sm"
                              .value=${sourcingParams.revenueMin ?? ''}
                              placeholder="min"
                              @input=${(e: Event) => {
                                const raw = (e.target as HTMLInputElement).value;
                                const value = raw === '' ? null : Number(raw);
                                if (value !== null && (value < 0 || !Number.isFinite(value))) return;
                                setSourcingParam('revenueMin', value);
                              }}
                            />
                            <span class="text-base-content/40 shrink-0 text-xs">—</span>
                            <input
                              type="number"
                              min="0"
                              max="1000"
                              class="input input-bordered flex-1 text-sm"
                              .value=${sourcingParams.revenueMax ?? ''}
                              placeholder="max"
                              @input=${(e: Event) => {
                                const raw = (e.target as HTMLInputElement).value;
                                const value = raw === '' ? null : Number(raw);
                                if (value !== null && (value < 0 || !Number.isFinite(value))) return;
                                setSourcingParam('revenueMax', value);
                              }}
                            />
                          </div>
                        </div>
                        ${
                          // Rendered ONLY when companies were actually given. An
                          // unconstrained dimension is never enumerated: with no
                          // company list this shows nothing at all, rather than
                          // implying the search is scoped to something.
                          sourcingParams.organizationNames.length > 0
                            ? html`
                              <div class="form-control md:col-span-2">
                                <span class="label-text mb-1 text-xs font-medium">
                                  Companies
                                  <span class="text-base-content/50 font-normal"
                                    >— searching only inside these
                                    ${sourcingParams.organizationNames.length}</span
                                  >
                                </span>
                                <div class="border-base-300 flex flex-wrap items-center gap-1.5 rounded-lg border p-2">
                                  ${sourcingParams.organizationNames.map(
                                    name => html`
                                      <span class="badge badge-outline gap-1">
                                        ${name}
                                        <button
                                          class="hover:text-error leading-none"
                                          title="Remove ${name}"
                                          @click=${() =>
                                            setSourcingParam(
                                              'organizationNames',
                                              sourcingParams.organizationNames.filter(n => n !== name),
                                            )}
                                        >
                                          <span class="icon-[material-symbols--close] align-text-bottom text-sm"></span>
                                        </button>
                                      </span>
                                    `,
                                  )}
                                  <button
                                    class="btn btn-ghost btn-xs ml-auto"
                                    title="Stop restricting the search to a company list"
                                    @click=${() => setSourcingParam('organizationNames', [])}
                                  >
                                    Clear all
                                  </button>
                                </div>
                              </div>
                            `
                            : nothing
                        }
                      </div>
                    </div>

                    <div class="border-base-300 rounded-xl border p-5">
                      <div class="mb-4 flex items-start gap-3">
                        <span
                          class="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                        >
                          <span class="icon-[material-symbols--person-search] text-xl"></span>
                        </span>
                        <div>
                          <h3 class="font-semibold">3. Target contact</h3>
                          <p class="text-base-content/70 text-sm">Identify the people we should contact.</p>
                        </div>
                      </div>
                      <div class="grid gap-3 md:grid-cols-2">
                        <div class="form-control md:col-span-2">
                          <span class="label-text mb-1 text-xs font-medium">Job Title</span>
                          ${TextInput({
                            value: sourcingParams.jobTitle,
                            onChanged: value => setSourcingParam('jobTitle', value),
                            onPicked: value => setSourcingParam('jobTitle', value),
                            placeholder: 'e.g. CFO, VP of Finance, Head of Operations',
                          })}
                        </div>
                        <div class="form-control">
                          <span class="label-text mb-1 text-xs font-medium">Department</span>
                          ${DropdownSelectionInput({
                            multi: true,
                            selectables: DEPARTMENTS,
                            value: sourcingParams.department,
                            onPicked: value => setSourcingParam('department', value ?? []),
                            placeholder: 'Select departments...',
                          })}
                        </div>
                        <div class="form-control">
                          <span class="label-text mb-1 text-xs font-medium">Person Location</span>
                          ${DropdownSelectionInput({
                            multi: true,
                            selectables: ALL_COUNTRY_NAMES,
                            value: sourcingParams.personLocation,
                            onPicked: value => setSourcingParam('personLocation', value ?? []),
                            placeholder: 'Search countries...',
                            emptyState: 'No countries found',
                          })}
                        </div>
                      </div>
                    </div>

                    <!--
                      A file that STEERS the search instead of supplying leads.
                      The lead importer above needs an email or LinkedIn column
                      and rejects a company list outright; this reads the same
                      file as constraints. Every column is shown — including the
                      ones we cannot search on — because a column silently
                      dropped looks exactly like a column that was applied.
                    -->
                    <div class="border-base-300 rounded-xl border p-5">
                      <div class="mb-4 flex items-start gap-3">
                        <span
                          class="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                        >
                          <span class="icon-[material-symbols--upload-file] text-xl"></span>
                        </span>
                        <div class="min-w-0 flex-1">
                          <h3 class="font-semibold">
                            4. Narrow it with a file
                            <span class="text-base-content/50 text-sm font-normal">(optional)</span>
                          </h3>
                          <p class="text-base-content/70 text-sm">
                            Upload companies, sites or segments — not leads. We read every column and use the ones
                            that match a search filter.
                          </p>
                        </div>
                        ${
                          csvFileName
                            ? html`<button class="btn btn-ghost btn-xs shrink-0" @click=${clearSourcingFile}>
                              Remove
                            </button>`
                            : nothing
                        }
                      </div>

                      ${
                        csvFields.length === 0
                          ? html`
                            <button
                              class="btn btn-outline btn-sm gap-1"
                              ?disabled=${csvReading}
                              @click=${pickSourcingFile}
                            >
                              ${
                                csvReading
                                  ? html`<span class="loading loading-spinner loading-xs"></span> Reading…`
                                  : html`<span class="icon-[material-symbols--upload] text-base"></span>`
                              }
                              Choose a file
                            </button>
                          `
                          : html`
                            <div class="flex flex-col gap-3">
                              <p class="text-base-content/60 text-xs">
                                <span
                                  class="icon-[material-symbols--description-outline] align-text-bottom text-sm"
                                ></span>
                                ${csvFileName} — ${csvFields.length}
                                column${csvFields.length === 1 ? '' : 's'}
                              </p>
                              ${csvFields.map(
                                f => html`
                                  <div class="border-base-300 rounded-lg border p-2">
                                    <div class="mb-1 flex flex-wrap items-center gap-2">
                                      <span class="text-xs font-medium">${f.header}</span>
                                      <span class="text-base-content/40 text-xs">
                                        ${f.values.length} distinct${f.truncated ? ' (first 200 read)' : ''}
                                      </span>
                                      <select
                                        class="select select-bordered select-xs ml-auto"
                                        @change=${(e: Event) =>
                                          setCsvFieldSlot(f.index, (e.target as HTMLSelectElement).value)}
                                      >
                                        <option value=${UNMAPPED_SLOT} ?selected=${f.slot === UNMAPPED_SLOT}>
                                          Don't use
                                        </option>
                                        ${CSV_FIELD_SLOTS.map(
                                          slot => html`<option value=${slot} ?selected=${f.slot === slot}>
                                            ${CSV_FIELD_SLOT_LABELS[slot]}
                                          </option>`,
                                        )}
                                      </select>
                                    </div>
                                    ${
                                      f.slot === UNMAPPED_SLOT
                                        ? html`<p class="text-base-content/40 text-xs">
                                          Not used for sourcing — there is no search filter for this.
                                        </p>`
                                        : html`
                                          <div class="flex flex-wrap items-center gap-1">
                                            ${f.selected.slice(0, CSV_CHIPS_SHOWN).map(
                                              value => html`
                                                <span class="badge badge-ghost badge-sm gap-1">
                                                  ${value}
                                                  <button
                                                    class="hover:text-error leading-none"
                                                    title="Remove ${value}"
                                                    @click=${() => removeCsvFieldValue(f.index, value)}
                                                  >
                                                    <span
                                                      class="icon-[material-symbols--close] align-text-bottom text-xs"
                                                    ></span>
                                                  </button>
                                                </span>
                                              `,
                                            )}
                                            ${
                                              f.selected.length > CSV_CHIPS_SHOWN
                                                ? html`<span class="text-base-content/50 text-xs"
                                                  >+${f.selected.length - CSV_CHIPS_SHOWN} more (all are used)</span
                                                >`
                                                : nothing
                                            }
                                            ${
                                              f.selected.length === 0
                                                ? html`<span class="text-base-content/40 text-xs"
                                                  >Empty — this column no longer narrows the search.</span
                                                >`
                                                : nothing
                                            }
                                          </div>
                                        `
                                    }
                                  </div>
                                `,
                              )}
                              <div class="flex flex-wrap items-center gap-2">
                                <button class="btn btn-primary btn-sm gap-1" @click=${applySourcingFile}>
                                  <span class="icon-[material-symbols--filter-alt] text-base"></span>
                                  Apply to the search
                                </button>
                                <span class="text-base-content/50 text-xs">
                                  Adds what you kept to the filters above, then refreshes the preview.
                                </span>
                              </div>
                            </div>
                          `
                      }
                      ${csvNotice ? html`<p class="text-warning mt-3 text-xs">${csvNotice}</p>` : nothing}
                    </div>
                  </div>

                  <div class="border-base-300 bg-base-100 sticky top-4 flex max-h-[720px] flex-col rounded-xl border">
                    <div class="border-base-300 border-b p-5">
                      <div class="flex flex-wrap items-start justify-between gap-2">
                        <div class="min-w-0">
                          <h3 class="font-semibold">5. Lead preview</h3>
                          <p class="text-base-content/60 text-sm">
                            ${
                              sourcingPreview.totalEntries === null
                                ? html`Sample leads from our sourcing preview.`
                                : html`Sample leads from
                                    <span
                                      title="A live estimate from our data source. The audience your ICP maps to is fixed, but this headline number can vary slightly between refreshes."
                                      >~${sourcingPreview.totalEntries.toLocaleString()}</span
                                    >
                                    matching contacts
                                    <span class="text-base-content/40">(estimate)</span>.`
                            }
                          </p>
                        </div>
                        <div class="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            class="btn btn-outline btn-primary btn-xs gap-1"
                            ?disabled=${sourcingPreview.loading}
                            @click=${() => void refreshSourcingPreview()}
                          >
                            ${
                              sourcingPreview.loading
                                ? html`<span class="loading loading-spinner loading-xs"></span>`
                                : html`<span class="icon-[material-symbols--refresh] text-sm"></span>`
                            }
                            Refresh
                          </button>
                          <button
                            type="button"
                            class="btn btn-outline btn-secondary btn-xs gap-1"
                            title="Download the full configured Lead count as CSV with real name/email/LinkedIn (not just the preview sample) — enriches each lead, costs ~1 Apollo credit per lead"
                            ?disabled=${csvExporting || sourcingPreview.loading}
                            @click=${() => void downloadSourcedLeadsCsv()}
                          >
                            ${
                              csvExporting
                                ? html`<span class="loading loading-spinner loading-xs"></span>`
                                : html`<span class="icon-[material-symbols--download] text-sm"></span>`
                            }
                            Download CSV
                          </button>
                          <span class="badge badge-primary badge-outline badge-sm whitespace-nowrap"
                            >${sourcingPreview.leads.length} shown</span
                          >
                        </div>
                        <div class="form-control md:col-span-2">
                          <span class="label-text mb-1 text-xs font-medium">Lead count</span>
                          <input
                            class="input input-bordered w-full"
                            type="number"
                            min="1"
                            max=${MAX_APOLLO_LEAD_LIMIT}
                            step="1"
                            .value=${sourcingParams.leadLimit}
                            @change=${(e: Event) =>
                              setSourcingParam('leadLimit', normalizeLeadLimit((e.target as HTMLInputElement).value))}
                          />
                          <span class="text-base-content/50 mt-1 text-xs">
                            Preview shows 5 leads. The campaign will source
                            ${sourcingParams.leadLimit || DEFAULT_APOLLO_LEAD_LIMIT} leads (capped at
                            ${MAX_APOLLO_LEAD_LIMIT}) that have the contact data your sequence needs — email address,
                            LinkedIn URL, or both. Leads missing it are replaced automatically while sourcing.
                          </span>
                        </div>
                      </div>
                      ${(() => {
                        const af = readAppliedFilters();
                        if (!af) return nothing;
                        const groups = appliedFilterGroups();
                        return html`
                          <div class="bg-base-200/50 mt-3 rounded-lg p-3">
                            ${
                              af.mappingFailed
                                ? html`
                                  <div class="alert alert-warning mb-2 p-2 text-xs">
                                    <span class="icon-[material-symbols--warning-outline] text-base"></span>
                                    <span>
                                      Your ICP could <strong>not</strong> be interpreted — this search fell back to a
                                      raw text match, which usually returns few or poor results. Click
                                      <strong>Re-interpret ICP</strong> to try again.
                                    </span>
                                  </div>
                                `
                                : nothing
                            }
                            <div class="flex flex-wrap items-center justify-between gap-2">
                              <div class="text-base-content/60 text-xs font-medium tracking-wide uppercase">
                                ICP interpretation
                              </div>
                              <button
                                type="button"
                                class="btn btn-ghost btn-xs gap-1"
                                title="Unhappy with the interpretation? Ask the AI to re-interpret your ICP from scratch. The new interpretation replaces the current one."
                                ?disabled=${sourcingPreview.loading}
                                @click=${() => void reinterpretIcp()}
                              >
                                <span class="icon-[material-symbols--autorenew] text-sm"></span> Re-interpret ICP
                              </button>
                            </div>
                            <p class="text-base-content/50 mt-1 text-xs">
                              Your ICP was translated into the filter fields on the left — adjust them there.
                            </p>
                            ${
                              groups.length > 0
                                ? html`
                                  <div class="mt-2 space-y-1.5">
                                    ${groups.map(
                                      g => html`
                                        <div class="flex flex-wrap items-baseline gap-1">
                                          <span class="text-base-content/50 w-24 shrink-0 text-xs">${g.label}</span>
                                          ${g.values.map(
                                            v =>
                                              html`<span class="badge badge-outline badge-sm max-w-56 truncate"
                                                >${v}</span
                                              >`,
                                          )}
                                        </div>
                                      `,
                                    )}
                                  </div>
                                  <p class="text-base-content/40 mt-2 text-[0.7rem]">
                                    These additional signals have no form field and are applied automatically.
                                  </p>
                                `
                                : nothing
                            }
                          </div>
                        `;
                      })()}
                    </div>
                    <div class="min-h-80 flex-1 overflow-y-auto p-3">
                      ${
                        sourcingPreview.loading
                          ? html`
                            <div class="text-base-content/60 flex h-64 items-center justify-center gap-2 text-sm">
                              <span class="loading loading-spinner loading-sm"></span>
                              Fetching sample leads…
                            </div>
                          `
                          : sourcingPreview.error
                            ? html`
                              <div class="alert alert-error text-sm">
                                <span class="icon-[material-symbols--error] text-lg"></span>
                                <span>${sourcingPreview.error}</span>
                              </div>
                            `
                            : sourcingPreview.leads.length === 0
                              ? html`
                                <div
                                  class="text-base-content/60 flex h-64 items-center justify-center text-center text-sm"
                                >
                                  Leads will appear here after you enter a search and refresh.
                                </div>
                              `
                              : curatedLeads.length === 0
                                ? html`
                                  <div
                                    class="text-base-content/60 flex h-64 flex-col items-center justify-center gap-2 text-center text-sm"
                                  >
                                    <span>You've removed every sourced lead.</span>
                                    <button type="button" class="btn btn-ghost btn-xs gap-1" @click=${restoreRemovedLeads}>
                                      <span class="icon-[material-symbols--undo] text-sm"></span>Restore removed
                                    </button>
                                  </div>
                                `
                                : html`
                                <div class="flex flex-col gap-2">
                                  <div class="flex flex-wrap items-center justify-between gap-2 px-0.5">
                                    <span class="text-base-content/60 text-xs">
                                      Showing ${curatedLeads.length} sourced
                                      lead${curatedLeads.length === 1 ? '' : 's'}${
                                        removedApolloPersonIds.length > 0
                                          ? html` · ${removedApolloPersonIds.length} removed`
                                          : nothing
                                      }
                                    </span>
                                    ${
                                      removedApolloPersonIds.length > 0
                                        ? html`<button
                                            type="button"
                                            class="btn btn-ghost btn-xs gap-1"
                                            @click=${restoreRemovedLeads}
                                          >
                                            <span class="icon-[material-symbols--undo] text-sm"></span>Restore removed
                                          </button>`
                                        : nothing
                                    }
                                  </div>
                                  ${curatedLeads.map(lead => {
                                    const enriched = lead.apolloPersonId ? detailCache[lead.apolloPersonId] : null;
                                    const firstName = enriched?.firstName ?? lead.firstName;
                                    const lastName = enriched?.lastName ?? lead.lastName;
                                    return html`
                                      <div class="bg-base-200/60 rounded-xl p-3">
                                        <div class="flex items-center justify-between gap-2">
                                          <div class="flex min-w-0 items-center gap-3">
                                            <div
                                              class="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                                            >
                                              ${firstName.slice(0, 1)}${lastName.slice(0, 1)}
                                            </div>
                                            <div class="min-w-0">
                                              <div class="truncate text-sm font-semibold">${firstName} ${lastName}</div>
                                              <div class="text-base-content/60 truncate text-xs">
                                                ${enriched?.company ?? lead.company}
                                              </div>
                                            </div>
                                          </div>
                                          ${
                                            lead.apolloPersonId
                                              ? html`
                                                <div class="flex shrink-0 items-center gap-1">
                                                  <button
                                                    type="button"
                                                    class="btn btn-ghost btn-xs btn-square shrink-0"
                                                    ?disabled=${detailLoadingId === lead.apolloPersonId}
                                                    @click=${(e: Event) => {
                                                      e.stopPropagation();
                                                      void fetchLeadDetail(lead.apolloPersonId!);
                                                    }}
                                                  >
                                                    ${
                                                      detailLoadingId === lead.apolloPersonId
                                                        ? html`<span class="loading loading-spinner loading-xs"></span>`
                                                        : html`<span class="icon-[material-symbols--info] text-sm"></span>`
                                                    }
                                                  </button>
                                                  <button
                                                    type="button"
                                                    class="btn btn-ghost btn-xs btn-square text-error/70 hover:text-error shrink-0"
                                                    title="Remove this lead — it won't be sourced for the campaign"
                                                    @click=${(e: Event) => {
                                                      e.stopPropagation();
                                                      removeSourcedLead(lead.apolloPersonId);
                                                    }}
                                                  >
                                                    <span class="icon-[material-symbols--close] text-sm"></span>
                                                  </button>
                                                </div>
                                              `
                                              : nothing
                                          }
                                        </div>
                                        <div class="text-base-content/70 mt-2 truncate text-xs">
                                          ${enriched?.jobTitle ?? lead.jobTitle}
                                        </div>
                                        ${
                                          lead.industry || lead.employeeCount
                                            ? html`
                                              <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                                                ${
                                                  lead.industry
                                                    ? html`<span class="badge badge-ghost badge-xs">
                                                        ${lead.industry}
                                                      </span>`
                                                    : nothing
                                                }
                                                ${
                                                  lead.employeeCount
                                                    ? html`<span
                                                        class="badge badge-ghost badge-xs"
                                                        title="Verified company size"
                                                      >
                                                        ${lead.employeeCount.toLocaleString('en-US')} employees
                                                      </span>`
                                                    : nothing
                                                }
                                              </div>
                                            `
                                            : nothing
                                        }
                                      </div>
                                    `;
                                  })}
                                  ${
                                    sourcingExhausted
                                      ? html`<div class="text-base-content/40 py-1 text-center text-xs">
                                          No more leads to load.
                                        </div>`
                                      : html`<button
                                          type="button"
                                          class="btn btn-outline btn-sm mt-1 gap-1.5"
                                          ?disabled=${loadingMoreLeads}
                                          @click=${() => void loadMoreSourcedLeads()}
                                        >
                                          ${
                                            loadingMoreLeads
                                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                                              : html`<span class="icon-[material-symbols--add] text-base"></span>`
                                          }
                                          Load ${PREVIEW_PAGE_SIZE} more
                                        </button>`
                                  }
                                </div>
                              `
                      }
                    </div>
                  </div>
                </div>
              `
              : nothing
          }
          ${
            selectedLeadSource === 'hubspot'
              ? html`
                <div class="border-warning/20 bg-warning/5 flex flex-col gap-3 rounded-xl border p-5">
                  <div class="flex items-start gap-3">
                    <span
                      class="bg-warning/10 text-warning flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                    >
                      <span class="icon-[material-symbols--hub] text-xl"></span>
                    </span>
                    <div>
                      <h3 class="font-semibold">Use CRM: HubSpot list</h3>
                      <p class="text-base-content/70 text-sm">Pull contacts from a HubSpot list into this campaign.</p>
                    </div>
                  </div>
                  ${
                    hubspotAvailable
                      ? html`
                        <div class="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            class="btn btn-outline btn-warning btn-sm gap-2"
                            @click=${() => void openHubspotDialog()}
                          >
                            <span class="icon-[material-symbols--hub] text-base"></span>
                            Import from HubSpot list
                          </button>
                          <span class="text-base-content/40 text-xs"
                            >Pulls contacts from a HubSpot list (up to 2000).</span
                          >
                        </div>
                      `
                      : html`
                        <div class="alert alert-warning text-sm">
                          <span class="icon-[material-symbols--info] text-lg"></span>
                          <span>HubSpot import is not available for this workspace yet.</span>
                        </div>
                      `
                  }
                </div>
              `
              : nothing
          }
          ${
            selectedLeadSource === 'csv'
              ? html`
                <div class="flex flex-col gap-3">
                  <p class="text-base-content/70 text-sm">
                    Upload a CSV or Excel file with target leads. Each lead needs at least an
                    <code class="bg-base-200 rounded px-1 text-xs">email</code> or a
                    <code class="bg-base-200 rounded px-1 text-xs">linkedin_url</code>. Recommended:
                    <code class="bg-base-200 rounded px-1 text-xs">first_name</code>,
                    <code class="bg-base-200 rounded px-1 text-xs">last_name</code>,
                    <code class="bg-base-200 rounded px-1 text-xs">company</code>,
                    <code class="bg-base-200 rounded px-1 text-xs">job_title</code>. Optional:
                    <code class="bg-base-200 rounded px-1 text-xs">industry</code>.
                  </p>
                  <div
                    class="${
                      dragging
                        ? 'border-primary bg-primary/5'
                        : 'border-base-300 hover:border-primary/50 hover:bg-base-200/50'
                    } flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition"
                    @click=${handleFileSelect}
                    @dragover=${(e: DragEvent) => {
                      e.preventDefault();
                      setDragging(true);
                    }}
                    @dragleave=${() => setDragging(false)}
                    @drop=${handleDrop}
                  >
                    <span
                      class="icon-[material-symbols--upload-file] ${
                        dragging ? 'text-primary' : 'text-base-content/30'
                      } text-4xl"
                    ></span>
                    <p class="${dragging ? 'text-primary' : 'text-base-content/70'} font-medium">
                      ${dragging ? 'Drop your file here' : 'Click or drag a CSV / Excel file here'}
                    </p>
                    <span class="text-base-content/40 text-xs"
                      >.csv, .xlsx, .xls (comma, semicolon, or tab separated)</span
                    >
                  </div>
                </div>
              `
              : nothing
          }
          ${
            parseError
              ? html`
                <div class="alert alert-error text-sm">
                  <span class="icon-[material-symbols--error] text-lg"></span>
                  <pre class="whitespace-pre-wrap">${parseError}</pre>
                </div>
              `
              : nothing
          }
          ${
            lastImportSummary && leads.length > 0
              ? html`
                <div class="alert alert-success text-sm">
                  <span class="icon-[material-symbols--check-circle] text-lg"></span>
                  <span>${lastImportSummary} — <strong>${leads.length}</strong> total</span>
                </div>
              `
              : fileName && leads.length > 0
                ? html`
                  <div class="alert alert-success text-sm">
                    <span class="icon-[material-symbols--check-circle] text-lg"></span>
                    <span>Loaded <strong>${leads.length}</strong> leads from <strong>${fileName}</strong></span>
                  </div>
                `
                : nothing
          }
        </div>
      </div>

      <!-- Leads Preview (if any) -->
      ${
        leads.length > 0
          ? html`
            <div class="card bg-base-100 shadow-sm">
              <div class="card-body flex flex-col gap-4">
                <div class="flex items-center justify-between">
                  <h2 class="card-title text-lg">Leads Preview</h2>
                  <button class="btn btn-ghost btn-sm text-error" @click=${clearLeads}>
                    <span class="icon-[material-symbols--delete] text-base"></span>
                    Clear
                  </button>
                </div>

                ${stats()}

                <div class="overflow-x-auto">
                  <table class="table-sm table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Company</th>
                        <th>Title</th>
                        <th>LinkedIn</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${leads.map(
                        lead => html`
                          <tr>
                            <td class="font-medium">${lead.firstName} ${lead.lastName}</td>
                            <td class="text-base-content/70">${lead.email}</td>
                            <td>${lead.company}</td>
                            <td class="text-base-content/70">${lead.jobTitle}</td>
                            <td>
                              ${
                                lead.linkedinUrl
                                  ? html`<span class="icon-[material-symbols--check-circle] text-success"></span>`
                                  : html`<span class="icon-[material-symbols--remove] text-base-content/30"></span>`
                              }
                            </td>
                            <td>
                              ${
                                lead.source === 'hubspot'
                                  ? html`<span
                                    class="badge badge-sm badge-warning gap-1"
                                    title=${lead.crmContactId ? `HubSpot id ${lead.crmContactId}` : 'From HubSpot'}
                                    >HubSpot</span
                                  >`
                                  : lead.source === 'csv'
                                    ? html`<span class="badge badge-sm badge-ghost">CSV</span>`
                                    : lead.source === 'eigen'
                                      ? html`<span class="badge badge-sm badge-primary badge-outline">Eigen</span>`
                                      : html`<span class="text-base-content/30">—</span>`
                              }
                            </td>
                          </tr>
                        `,
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Lead detail modal -->
      ${
        detailModalLead
          ? html`
            <div
              class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              @click=${(e: MouseEvent) => {
                if (e.target === e.currentTarget) setDetailModalLead(null);
              }}
            >
              <div
                class="bg-base-100 flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-2xl"
              >
                <div class="border-base-300 flex items-center justify-between border-b px-5 py-3">
                  <div class="flex items-center gap-2">
                    <span class="icon-[material-symbols--info] text-primary text-xl"></span>
                    <h3 class="font-semibold">${detailModalLead.firstName} ${detailModalLead.lastName}</h3>
                  </div>
                  <button class="btn btn-ghost btn-sm btn-circle" @click=${() => setDetailModalLead(null)}>
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div class="overflow-y-auto p-5">
                  <div class="grid gap-4 sm:grid-cols-2">
                    <div>
                      <div class="text-base-content/50 text-xs">Email</div>
                      <div class="text-sm font-medium">${detailModalLead.email}</div>
                      ${
                        detailModalLead.emailStatus
                          ? html`<span class="badge badge-ghost badge-xs mt-1">${detailModalLead.emailStatus}</span>`
                          : nothing
                      }
                    </div>
                    <div>
                      <div class="text-base-content/50 text-xs">Phone</div>
                      <div class="text-sm font-medium">${detailModalLead.phone || '—'}</div>
                    </div>
                    <div>
                      <div class="text-base-content/50 text-xs">Job Title</div>
                      <div class="text-sm font-medium">${detailModalLead.jobTitle || '—'}</div>
                    </div>
                    <div>
                      <div class="text-base-content/50 text-xs">Company</div>
                      <div class="text-sm font-medium">${detailModalLead.company || '—'}</div>
                    </div>
                    <div>
                      <div class="text-base-content/50 text-xs">Industry</div>
                      <div class="text-sm font-medium">${detailModalLead.industry || '—'}</div>
                    </div>
                    <div>
                      <div class="text-base-content/50 text-xs">Location</div>
                      <div class="text-sm font-medium">
                        ${
                          [detailModalLead.city, detailModalLead.state, detailModalLead.country]
                            .filter(Boolean)
                            .join(', ') || '—'
                        }
                      </div>
                    </div>
                    ${
                      detailModalLead.linkedinUrl
                        ? html`
                          <div class="sm:col-span-2">
                            <div class="text-base-content/50 text-xs">LinkedIn</div>
                            <a
                              href=${detailModalLead.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              class="link link-primary text-sm"
                            >
                              ${detailModalLead.linkedinUrl}
                            </a>
                          </div>
                        `
                        : nothing
                    }
                  </div>
                </div>
              </div>
            </div>
          `
          : nothing
      }

      <!-- HubSpot list import dialog -->
      ${
        hubspotDialogOpen
          ? html`
            <div
              class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              @click=${(e: MouseEvent) => {
                if (e.target === e.currentTarget) setHubspotDialogOpen(false);
              }}
            >
              <div
                class="bg-base-100 flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl shadow-2xl"
              >
                <div class="border-base-300 flex items-center justify-between border-b px-5 py-3">
                  <div class="flex items-center gap-2">
                    <span class="icon-[material-symbols--hub] text-warning text-xl"></span>
                    <h3 class="font-semibold">Import contacts from a HubSpot list</h3>
                  </div>
                  <button class="btn btn-ghost btn-sm btn-circle" @click=${() => setHubspotDialogOpen(false)}>
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div class="overflow-y-auto p-4">
                  ${
                    hubspotError
                      ? html`
                        <div class="alert alert-error mb-3 text-sm">
                          <span class="icon-[material-symbols--error]"></span>
                          <span>${hubspotError}</span>
                        </div>
                      `
                      : nothing
                  }
                  ${
                    hubspotLoadingLists
                      ? html`<div class="text-base-content/60 flex items-center justify-center py-10">
                        <span class="loading loading-spinner loading-md"></span><span class="ml-2">Loading lists…</span>
                      </div>`
                      : (hubspotLists?.length ?? 0) === 0
                        ? html`<div class="text-base-content/60 py-8 text-center text-sm">
                          No lists found in this HubSpot portal.
                        </div>`
                        : html`<div class="divide-base-300 flex flex-col divide-y">
                          ${(hubspotLists ?? []).map(
                            l => html`
                              <div class="flex items-center justify-between gap-3 py-2.5">
                                <div class="min-w-0">
                                  <div class="truncate font-medium">${l.name}</div>
                                  <div class="text-base-content/50 text-xs">
                                    ${l.size !== undefined ? `${l.size} contacts` : 'size unknown'} · list ${l.listId}
                                  </div>
                                </div>
                                <button
                                  class="btn btn-warning btn-sm shrink-0 gap-1"
                                  ?disabled=${hubspotImporting !== null}
                                  @click=${() => void importHubspotList(l.listId, l.name)}
                                >
                                  ${
                                    hubspotImporting === l.listId
                                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                                      : html`<span class="icon-[material-symbols--download] text-base"></span>`
                                  }
                                  Import
                                </button>
                              </div>
                            `,
                          )}
                        </div>`
                  }
                </div>
              </div>
            </div>
          `
          : nothing
      }
    </div>
  `;
});
